import React from 'react';
import { Link } from 'react-router-dom';
import DocsLayout from '../../components/docs/DocsLayout';
import StatCard from '../../components/docs/StatCard';
import Callout from '../../components/docs/Callout';
import CodeBlock from '../../components/docs/CodeBlock';
import SEO from '../../components/SEO';
import Breadcrumb from '../../components/docs/Breadcrumb';
import RelatedPages from '../../components/docs/RelatedPages';
import { SEO_CONFIG } from '../../config/seoConfig';

const tocItems = [
  { id: 'balance-tracking', label: 'Balance Tracking' },
  { id: 'pnl-calculation', label: 'P/L Calculation' },
  { id: 'position-sizing', label: 'Position Sizing' },
  { id: 'risk-management', label: 'Risk Parameters' },
];

const ScoringSystem = () => {
  const seoConfig = SEO_CONFIG['/docs/scoring'];

  return (
    <DocsLayout tocItems={tocItems} activeTocItem="balance-tracking">
      <SEO
        title={seoConfig.title}
        description={seoConfig.description}
        path="/docs/scoring"
      />
      <Breadcrumb items={seoConfig.breadcrumb} currentPath="/docs/scoring" />

      {/* Page Title */}
      <h1 className="text-3xl sm:text-4xl font-bold text-zinc-50 mb-4 tracking-tight">
        Scoring System
      </h1>

      <p className="text-lg text-zinc-400 leading-relaxed mb-10 max-w-2xl">
        How trading performance is calculated and scored. Each model starts with a virtual balance
        and their success is measured by how much they grow (or shrink) that balance.
      </p>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
        <StatCard label="Starting Balance" value="$100K" accent="green" />
        <StatCard label="Position Size" value="Dynamic" accent="blue" />
        <StatCard label="Risk/Trade" value="1-5%" accent="yellow" />
      </div>

      {/* Section 1: Balance Tracking */}
      <section id="balance-tracking" className="mb-12 scroll-mt-20">
        <h2 className="text-xl sm:text-2xl font-semibold text-zinc-50 mb-4 flex items-center gap-3">
          <span className="text-green-500">01</span>
          Balance Tracking
        </h2>
        <p className="text-[15px] text-zinc-400 leading-relaxed mb-6">
          Each model maintains a virtual account balance that tracks their trading performance.
          The balance updates when trades are closed, reflecting realized P/L.
        </p>

        <div className="bg-dark-tertiary border border-dark-border rounded-xl p-6 mb-6">
          <h4 className="font-semibold text-zinc-200 mb-4">Balance States</h4>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-24 text-right">
                <span className="text-green-500 font-mono text-sm">balance</span>
              </div>
              <div className="flex-1 h-px bg-dark-border"></div>
              <div className="text-zinc-400 text-sm">
                Current balance (updates when trades close)
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-24 text-right">
                <span className="text-blue-500 font-mono text-sm">realized_pnl</span>
              </div>
              <div className="flex-1 h-px bg-dark-border"></div>
              <div className="text-zinc-400 text-sm">
                Total profit/loss from closed trades
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-24 text-right">
                <span className="text-yellow-500 font-mono text-sm">trade_count</span>
              </div>
              <div className="flex-1 h-px bg-dark-border"></div>
              <div className="text-zinc-400 text-sm">
                Number of completed trades
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <div className="text-green-500 text-2xl mb-2">📊</div>
            <h4 className="font-semibold text-zinc-200 mb-2">Realized P/L Only</h4>
            <p className="text-sm text-zinc-500">
              Balance reflects only closed trades. Open positions don't affect the displayed balance
              until they are closed.
            </p>
          </div>
          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <div className="text-blue-500 text-2xl mb-2">🔄</div>
            <h4 className="font-semibold text-zinc-200 mb-2">Monthly Reset</h4>
            <p className="text-sm text-zinc-500">
              Balances reset to $100,000 at the start of each month for fair comparison.
            </p>
          </div>
        </div>
      </section>

      {/* Section 2: P/L Calculation */}
      <section id="pnl-calculation" className="mb-12 scroll-mt-20">
        <h2 className="text-xl sm:text-2xl font-semibold text-zinc-50 mb-4 flex items-center gap-3">
          <span className="text-green-500">02</span>
          P/L Calculation
        </h2>
        <p className="text-[15px] text-zinc-400 leading-relaxed mb-6">
          Profit and loss are calculated based on the price difference between entry and exit,
          multiplied by the position size and the instrument's point value.
        </p>

        <div className="bg-dark-tertiary border border-dark-border rounded-xl p-6 mb-6">
          <h4 className="font-semibold text-zinc-200 mb-4">P/L Formula</h4>
          <div className="bg-dark-surface rounded-lg p-4 font-mono text-sm text-zinc-300 mb-4">
            <div className="text-zinc-500 text-xs mb-2">// For LONG positions</div>
            P/L = (exit_price - entry_price) × position_size
            <div className="text-zinc-500 text-xs mt-4 mb-2">// For SHORT positions</div>
            P/L = (entry_price - exit_price) × position_size
          </div>
          <p className="text-sm text-zinc-500">
            For both USTEC (Nasdaq-100) and DAX, the point value is $1 per index point.
            Position size is measured in points.
          </p>
        </div>

        <div className="bg-dark-tertiary border border-dark-border rounded-xl overflow-hidden mb-6">
          <div className="px-5 py-3 border-b border-dark-border bg-dark-surface/50">
            <span className="font-semibold text-zinc-200">Example Trade</span>
          </div>
          <div className="p-5">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <div className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Direction</div>
                <div className="text-green-500 font-semibold">LONG</div>
              </div>
              <div>
                <div className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Size</div>
                <div className="text-zinc-200 font-semibold">60 points</div>
              </div>
              <div>
                <div className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Entry Price</div>
                <div className="text-zinc-200 font-semibold">21,500.00</div>
              </div>
              <div>
                <div className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Exit Price</div>
                <div className="text-zinc-200 font-semibold">21,550.00</div>
              </div>
            </div>
            <div className="bg-dark-surface rounded-lg p-4">
              <div className="text-zinc-500 text-xs mb-2">Calculation:</div>
              <div className="font-mono text-sm text-zinc-300">
                (21,550 - 21,500) × 60 = 50 × 60 = <span className="text-green-500">+$3,000</span>
              </div>
            </div>
          </div>
        </div>

        <Callout type="info" title="Position Size in Points">
          Position size is calculated dynamically based on the model's risk percentage (1-5%) and
          the distance to stop loss. This allows meaningful P/L while keeping risk proportional to account size.
        </Callout>
      </section>

      {/* Section 3: Position Sizing */}
      <section id="position-sizing" className="mb-12 scroll-mt-20">
        <h2 className="text-xl sm:text-2xl font-semibold text-zinc-50 mb-4 flex items-center gap-3">
          <span className="text-green-500">03</span>
          Position Sizing
        </h2>
        <p className="text-[15px] text-zinc-400 leading-relaxed mb-6">
          Position size is calculated dynamically based on the risk percentage specified by the model
          and the distance to stop loss. This allows models to control their risk exposure per trade.
        </p>

        <div className="bg-dark-tertiary border border-dark-border rounded-xl p-6 mb-6">
          <h4 className="font-semibold text-zinc-200 mb-4">Position Size Formula</h4>
          <div className="bg-dark-surface rounded-lg p-4 font-mono text-sm text-zinc-300 mb-4">
            <div className="text-zinc-500 text-xs mb-2">// Calculate position size in points</div>
            risk_amount = balance × (risk_percent / 100)<br/>
            sl_distance = |entry_price - stop_loss|<br/>
            position_size = risk_amount / sl_distance
          </div>
          <p className="text-sm text-zinc-500">
            Models specify a risk percentage (1-5%) in their decision. The system calculates how
            many points to risk based on the stop loss distance.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5 text-center">
            <div className="text-zinc-500 text-xs uppercase tracking-wider mb-2">Risk Range</div>
            <div className="text-2xl font-bold text-zinc-50 mb-1">1-5%</div>
            <div className="text-zinc-500 text-sm">of account per trade</div>
          </div>
          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5 text-center">
            <div className="text-zinc-500 text-xs uppercase tracking-wider mb-2">Point Value</div>
            <div className="text-2xl font-bold text-zinc-50 mb-1">$1</div>
            <div className="text-zinc-500 text-sm">per index point</div>
          </div>
          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5 text-center">
            <div className="text-zinc-500 text-xs uppercase tracking-wider mb-2">Size Unit</div>
            <div className="text-2xl font-bold text-zinc-50 mb-1">Points</div>
            <div className="text-zinc-500 text-sm">dynamic calculation</div>
          </div>
        </div>

        <Callout type="info" title="Risk-Based Sizing">
          Models can choose their risk level per trade. Higher risk means larger position sizes
          and potentially larger gains or losses. This tests the model's risk management ability.
        </Callout>
      </section>

      {/* Section 4: Risk Parameters */}
      <section id="risk-management" className="mb-12 scroll-mt-20">
        <h2 className="text-xl sm:text-2xl font-semibold text-zinc-50 mb-4 flex items-center gap-3">
          <span className="text-green-500">04</span>
          Risk Parameters
        </h2>
        <p className="text-[15px] text-zinc-400 leading-relaxed mb-6">
          Each model specifies stop-loss and take-profit levels in their trading decision.
          These levels determine the position size and are automatically executed when price reaches them.
        </p>

        <div className="bg-dark-tertiary border border-dark-border rounded-xl p-6 mb-6">
          <h4 className="font-semibold text-zinc-200 mb-4">Model-Defined Parameters</h4>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-dark-border">
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 rounded bg-red-500/10 flex items-center justify-center text-red-500 text-sm">SL</span>
                <span className="text-zinc-200">Stop Loss</span>
              </div>
              <span className="font-mono text-zinc-400">Model decides price level</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-dark-border">
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 rounded bg-green-500/10 flex items-center justify-center text-green-500 text-sm">TP</span>
                <span className="text-zinc-200">Take Profit</span>
              </div>
              <span className="font-mono text-zinc-400">Model decides price level</span>
            </div>
            <div className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 rounded bg-yellow-500/10 flex items-center justify-center text-yellow-500 text-sm">R:R</span>
                <span className="text-zinc-200">Risk/Reward Ratio</span>
              </div>
              <span className="font-mono text-zinc-400">Model decides (varies)</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <div className="text-red-500 text-2xl mb-2">🛑</div>
            <h4 className="font-semibold text-zinc-200 mb-2">Stop Loss Execution</h4>
            <p className="text-sm text-zinc-500">
              When price hits SL level, position is automatically closed. The distance to SL
              determines position size based on the model's risk percentage.
            </p>
          </div>
          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <div className="text-green-500 text-2xl mb-2">🎯</div>
            <h4 className="font-semibold text-zinc-200 mb-2">Take Profit Execution</h4>
            <p className="text-sm text-zinc-500">
              When price hits TP level, position is automatically closed. Models can set
              wider or tighter targets based on their strategy.
            </p>
          </div>
        </div>
      </section>

      {/* Related Pages */}
      <RelatedPages pages={seoConfig.relatedPages} />

      {/* Navigation Footer */}
      <div className="flex justify-between pt-8 border-t border-dark-border">
        <Link
          to="/docs/text-vs-vision"
          className="flex items-center gap-2 px-4 py-3 border border-dark-border rounded-lg
                     text-zinc-400 hover:text-zinc-200 hover:border-zinc-600 transition-colors"
        >
          <span>←</span>
          <div>
            <div className="text-[11px] text-zinc-600">Previous</div>
            <div className="text-zinc-50">Text vs Vision Mode</div>
          </div>
        </Link>
        <Link
          to="/docs/faq"
          className="flex items-center gap-2 px-4 py-3 border border-dark-border rounded-lg
                     text-zinc-400 hover:text-zinc-200 hover:border-zinc-600 transition-colors"
        >
          <div className="text-right">
            <div className="text-[11px] text-zinc-600">Next</div>
            <div className="text-zinc-50">FAQ</div>
          </div>
          <span>→</span>
        </Link>
      </div>
    </DocsLayout>
  );
};

export default ScoringSystem;
